import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  UserCredential,
  User as FirebaseUser
} from "firebase/auth";
import { doc, setDoc, getDoc, collection } from "firebase/firestore";
import { auth, db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/lib/logger";

// Types for our authentication context
type User = {
  id: string;
  email: string | null;
  name: string;
  role: "admin" | "co-admin" | "user" | "developer" | "berater";
  active?: boolean;
  position?: string;
  tenantId: string;
};

type AuthContextType = {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, options?: { tenantId?: string; role?: User["role"]; companyName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const createUserObject = async (firebaseUser: FirebaseUser): Promise<User> => {
    try {
      const userRef = doc(db, "users", firebaseUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        return {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: userData.name || '',
          role: userData.role || 'user',
          active: userData.active !== false,
          position: userData.position || '',
          tenantId: userData.tenantId || LEGACY_TENANT_ID,
        };
      } else {
        return {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: '',
          role: 'user',
          active: true,
          position: '',
          tenantId: LEGACY_TENANT_ID,
        };
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: '',
        role: 'user',
        active: true,
        position: '',
        tenantId: LEGACY_TENANT_ID,
      };
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userObj = await createUserObject(user);
          setCurrentUser(userObj);
        } catch (error) {
          console.error("Error fetching user data:", error);
          setCurrentUser({
            id: user.uid,
            email: user.email,
            name: '',
            role: 'user',
            active: true,
            position: '',
            tenantId: LEGACY_TENANT_ID,
          });
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login function using Firebase
  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userObj = await createUserObject(userCredential.user);

      // Check if the user is active
      if (userObj.active === false) {
        // Sign out the user immediately
        await signOut(auth);
        throw new Error("Ihr Konto wurde deaktiviert. Bitte kontaktieren Sie den Administrator.");
      }

      toast({
        title: "Erfolgreich angemeldet",
        description: `Willkommen zurück, ${userObj.name || 'Benutzer'}!`,
      });
    } catch (error: any) {
      logger.error("Login error:", error);
      let errorMessage = "Anmeldung fehlgeschlagen";

      if (error.message === "Ihr Konto wurde deaktiviert. Bitte kontaktieren Sie den Administrator.") {
        errorMessage = error.message;
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = "Ungültige E-Mail oder Passwort";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Zu viele Anmeldeversuche. Bitte versuchen Sie es später erneut.";
      }

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Register function using Firebase
  const register = async (name: string, email: string, password: string, options?: { tenantId?: string; role?: User["role"]; companyName?: string }) => {
    setLoading(true);
    try {
      // Create the user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Determine tenantId and role
      let tenantId = options?.tenantId;
      let role = options?.role || "user";

      // If companyName is provided, we are creating a new tenant
      if (options?.companyName) {
        // Generate a new tenant ID (using a new document ID from a 'tenants' collection placeholder)
        const tenantRef = doc(collection(db, "tenants"));
        tenantId = tenantRef.id;
        role = "admin"; // Creator of a company is admin

        // Create the tenant document
        await setDoc(tenantRef, {
          name: options.companyName,
          createdAt: new Date().toISOString(),
          createdBy: user.uid,
          ownerId: user.uid,
        });
      }

      // If no tenantId is provided and no company name, default to legacy (should not happen in new flow but for safety)
      if (!tenantId) {
        tenantId = LEGACY_TENANT_ID;
      }

      // Try to create a user document in Firestore
      try {
        await setDoc(doc(db, "users", user.uid), {
          name,
          email,
          role,
          position: options?.companyName ? "berater" : undefined, // Set position to berater for company creators
          createdAt: new Date().toISOString(),
          active: true, // New users are active by default
          tenantId,
        });
      } catch (firestoreError) {
        console.error("Firestore error during registration:", firestoreError);
        // Continue with success even if Firestore fails
        // The user is still created in Firebase Authentication
      }

      toast({
        title: "Registrierung erfolgreich",
        description: "Sie können sich jetzt anmelden.",
      });

      return;
    } catch (error: any) {
      console.error("Registration error:", error);
      let errorMessage = "Registrierung fehlgeschlagen";

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = "E-Mail wird bereits verwendet";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Das Passwort ist zu schwach";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Ungültige E-Mail";
      }

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Logout function using Firebase
  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);

      // Explicitly set currentUser to null to ensure UI updates immediately
      setCurrentUser(null);

      toast({
        title: "Abgemeldet",
        description: "Sie wurden erfolgreich abgemeldet.",
      });

      return;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Password reset function using Firebase
  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);

      toast({
        title: "Passwort-Reset angefordert",
        description: "Eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts wurde gesendet.",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      let errorMessage = "Passwort-Reset fehlgeschlagen";

      if (error.code === 'auth/user-not-found') {
        // For security reasons, we don't want to reveal if a user exists
        toast({
          title: "Passwort-Reset angefordert",
          description: "Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie eine E-Mail mit weiteren Anweisungen.",
        });
        return;
      }

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
